import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, LockOpen, ArrowRight } from 'lucide-react';
import Loading from '../components/Loading';
import { login, fetchCurrentUser } from '../services/authService';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import DarkModeToggle from '../components/common/DarkModeToggle';
import BorderGlow from '../components/common/BorderGlow';
import BrandStripes from '../components/common/BrandStripes';
import HexagonCornerDecoration from '../components/common/HexagonCornerDecoration';
import authImage from '../assets/images/auth/inicio.png';
import logo from '../assets/images/logo/logo.png';
const logoDark = `${process.env.PUBLIC_URL}/logo-comarca-blanco.png?v=2`;

const colors = {
    primary: '#3564db',
    primaryDark: '#420f54',
    primaryLight: '#3564db',
    secondary: '#3564db',
    secondaryLight: '#92b1ed',
    accent: '#ffd97c',
    accentDark: '#EA8406',
};

const Login = () => {
    const { isDark } = useTheme();
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [emailFocused, setEmailFocused] = useState(false);
    const [passFocused, setPassFocused] = useState(false);
    const [mounted, setMounted] = useState(false);
    const navigate = useNavigate();
    const { setUser, setIsAuthenticated } = useAuth();

    useEffect(() => { setTimeout(() => setMounted(true), 60); }, []);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const fields = new FormData(e.currentTarget);
        setError('');
        setIsLoading(true);
        try {
            const response = await login({
                email: String(fields.get('email') ?? ''),
                password: String(fields.get('password') ?? ''),
            });
            if (response.data.requires_2fa) { navigate('/verificar-acceso'); return; }
            const redirectUrl = response.data.redirect_url;
            const userData = await fetchCurrentUser();
            setUser(userData);
            setIsAuthenticated(true);
            navigate(redirectUrl);
        } catch (err: any) {
            setError(err.message || 'Error desconocido');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{
            fontFamily: "'DM Sans', sans-serif",
            minHeight: '100vh',
            display: 'flex',
            background: isDark ? '#0b0614' : '#f8fafc',
            position: 'relative',
            transition: 'background 0.3s ease',
        }}>
            {/* Toggle de modo oscuro en la esquina superior */}
            <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 100 }}>
                <DarkModeToggle />
            </div>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
                * { box-sizing: border-box; margin: 0; padding: 0; }

                @keyframes blobFloat {
                    0%, 100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
                    50% { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
                }
                @keyframes fadeUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to   { opacity: 1; }
                }
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes pulsePurple {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(97,37,123,0.35); }
                    50%       { box-shadow: 0 0 0 6px rgba(97,37,123,0); }
                }
                .blob-auth { animation: blobFloat 9s ease-in-out infinite; }
                .auth-input { transition: all 0.2s cubic-bezier(.16,1,.3,1); }
                .auth-input:focus { outline: none; }
                .auth-input::placeholder { color: #94a3b8; }
                .auth-btn-submit:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(97,37,123,0.4) !important; }
                .auth-btn-submit:active { transform: translateY(0); }
                .auth-link:hover { color: #3564db !important; }
            `}</style>

            {isLoading && <Loading text="Iniciando sesión..." fullScreen={true} />}

            {/* ── Panel izquierdo (decorativo) ── */}
            {/* ── Panel izquierdo (decorativo inspirado en el Landing Page) ── */}
            <div style={{
                display: 'none',
                flex: '0 0 50%',
                position: 'relative',
                overflow: 'hidden',
                background: isDark
                    ? 'linear-gradient(135deg, #0b0614 0%, #150a28 50%, #1f0b36 100%)'
                    : 'linear-gradient(135deg, #fdfbfd 0%, #faf3fe 50%, #f3e5f8 100%)',
                borderRight: isDark ? '1.5px solid rgba(193,52,139,0.22)' : '1.5px solid #ebdcf0',
            }}
                className="md-show"
            >
                <style>{`@media (min-width: 768px) { .md-show { display: flex !important; align-items: center; justify-content: center; } }`}</style>

                {/* Malla hexagonal minimalista con degradé horizontal suave */}
                <HexagonCornerDecoration
                    variant="horizontal-degrade"
                    direction="left-to-right"
                    minimal={true}
                    opacity={isDark ? 0.38 : 0.28}
                />

                {/* Resplandor ambiental suave y sutil */}
                <div style={{
                    position: 'absolute', top: '-60px', right: '-60px',
                    width: '380px', height: '380px', pointerEvents: 'none', zIndex: 0,
                    background: isDark
                        ? `radial-gradient(circle, rgba(193,52,139,0.14) 0%, transparent 70%)`
                        : `radial-gradient(circle, rgba(97,37,123,0.08) 0%, transparent 70%)`,
                }} />
                <div style={{
                    position: 'absolute', bottom: '-60px', left: '-60px',
                    width: '340px', height: '340px', pointerEvents: 'none', zIndex: 0,
                    background: isDark
                        ? `radial-gradient(circle, rgba(255,217,124,0.08) 0%, transparent 70%)`
                        : `radial-gradient(circle, rgba(255,217,122,0.12) 0%, transparent 70%)`,
                    borderRadius: '50%',
                }} />

                {/* Barra de colores corporativos en el borde inferior */}
                <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    height: '3px', zIndex: 10,
                    background: `linear-gradient(90deg, #3564db, #3564db, #3564db, #FFD97C, #EA8406)`,
                }} />

                {/* Contenido principal izquierdo */}
                <div style={{ position: 'relative', textAlign: 'center', padding: '40px 36px', maxWidth: '490px', zIndex: 2 }}>
                    {/* Badge corporativo */}
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '8px',
                        background: isDark ? 'rgba(193,52,139,0.18)' : 'rgba(97,37,123,0.08)',
                        border: isDark ? '1.5px solid rgba(193,52,139,0.35)' : '1.5px solid rgba(97,37,123,0.2)',
                        backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
                        borderRadius: '100px', padding: '6px 18px',
                        fontSize: '0.72rem', fontWeight: '800',
                        color: isDark ? '#FFD97C' : '#3564db',
                        letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '22px',
                    }}>
                        <span style={{
                            width: '7px', height: '7px', borderRadius: '50%',
                            background: isDark ? '#FFD97C' : '#3564db', display: 'inline-block',
                            boxShadow: isDark ? '0 0 8px #FFD97C' : '0 0 8px #3564db',
                        }} />
                        Co Marca Group · Coworking Hub
                    </span>

                    {/* Showcase Fotográfico con Glassmorphism (Reemplaza el cuadro gris vacío) */}
                    <div style={{
                        position: 'relative',
                        borderRadius: '26px', overflow: 'hidden',
                        boxShadow: isDark ? '0 24px 60px -10px rgba(0,0,0,0.8), 0 0 30px rgba(97,37,123,0.2)' : '0 24px 60px -10px rgba(97,37,123,0.18)',
                        marginBottom: '26px',
                        border: isDark ? '1.5px solid rgba(193,52,139,0.3)' : '1.5px solid rgba(255,255,255,0.8)',
                    }}>
                        <img
                            src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1000&q=80"
                            alt="Espacio Co Marca"
                            style={{ width: '100%', height: '240px', objectFit: 'cover', display: 'block' }}
                        />
                        {/* Overlay gradiente sobre la imagen para legibilidad y elegancia */}
                        <div style={{
                            position: 'absolute', inset: 0,
                            background: 'linear-gradient(to top, rgba(11,6,20,0.88) 0%, rgba(11,6,20,0.25) 55%, transparent 100%)',
                            pointerEvents: 'none',
                        }} />

                        {/* Tag de ubicación */}
                        <div style={{
                            position: 'absolute', top: '12px', left: '12px',
                            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: '8px', padding: '4px 10px',
                            fontSize: '0.7rem', fontWeight: '700', color: '#fff',
                            display: 'flex', alignItems: 'center', gap: '5px',
                        }}>
                            <span>📍</span> Calacoto · La Paz
                        </div>

                        {/* Floating glassmorphism pill con comunidad activa */}
                        <div style={{
                            position: 'absolute', bottom: '12px', left: '12px', right: '12px',
                            background: 'rgba(255,255,255,0.14)', backdropFilter: 'blur(14px)',
                            WebkitBackdropFilter: 'blur(14px)',
                            border: '1px solid rgba(255,255,255,0.25)',
                            borderRadius: '14px', padding: '9px 14px',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            color: '#fff',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ display: 'flex', marginLeft: '2px' }}>
                                    <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=80&q=80" alt="member" style={{ width: '22px', height: '22px', borderRadius: '50%', border: '2px solid #fff', objectFit: 'cover' }} />
                                    <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=80&q=80" alt="member" style={{ width: '22px', height: '22px', borderRadius: '50%', border: '2px solid #fff', marginLeft: '-6px', objectFit: 'cover' }} />
                                    <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=80&q=80" alt="member" style={{ width: '22px', height: '22px', borderRadius: '50%', border: '2px solid #fff', marginLeft: '-6px', objectFit: 'cover' }} />
                                </div>
                                <span style={{ fontSize: '0.74rem', fontWeight: '600' }}>+300 empresas conectadas</span>
                            </div>
                            <span style={{ fontSize: '0.68rem', fontWeight: '700', color: '#92b1ed', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#92b1ed', boxShadow: '0 0 6px #92b1ed' }} />
                                Activo
                            </span>
                        </div>
                    </div>

                    {/* Título & Subtítulo */}
                    <h2 style={{
                        fontSize: 'clamp(1.5rem, 2.4vw, 2.1rem)', fontWeight: '900',
                        color: isDark ? '#ffffff' : '#0f172a', letterSpacing: '-0.025em', lineHeight: 1.15, marginBottom: '10px',
                    }}>
                        El espacio donde Bolivia{' '}
                        <span style={{
                            background: 'linear-gradient(135deg, #3564db 0%, #EA8406 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}>
                            trabaja
                        </span>
                    </h2>
                    <p style={{ fontSize: '0.9rem', color: isDark ? '#cbd5e1' : '#64748b', lineHeight: '1.65', fontWeight: '400', textAlign: 'center', marginBottom: '16px' }}>
                        Puestos de cowork flexibles, oficinas privadas y salas de reunión equipadas con tecnología de punta para potenciar tus proyectos.
                    </p>

                    {/* Chips de amenities de Co Marca */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px', marginBottom: '20px' }}>
                        {[
                            { icon: '⚡', label: 'Wi-Fi 500 Mbps' },
                            { icon: '☕', label: 'Café de Especialidad' },
                            { icon: '🔒', label: 'Acceso 24/7' },
                            { icon: '🤝', label: 'Networking VIP' },
                        ].map((amenity) => (
                            <span
                                key={amenity.label}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                                    padding: '5px 12px', borderRadius: '100px',
                                    fontSize: '0.72rem', fontWeight: '700',
                                    background: isDark ? 'rgba(97, 37, 123, 0.25)' : 'rgba(97, 37, 123, 0.06)',
                                    border: isDark ? '1px solid rgba(193, 52, 139, 0.3)' : '1px solid rgba(97, 37, 123, 0.15)',
                                    color: isDark ? '#f1f5f9' : '#451759',
                                    backdropFilter: 'blur(6px)',
                                }}
                            >
                                <span>{amenity.icon}</span>
                                {amenity.label}
                            </span>
                        ))}
                    </div>

                    {/* Stats con diseño glassmorphic de tres columnas */}
                    <div style={{
                        display: 'flex', justifyContent: 'center', gap: '0',
                        background: isDark ? '#150d26' : '#ffffff',
                        borderRadius: '18px', border: isDark ? '1.5px solid rgba(193,52,139,0.28)' : '1.5px solid #edf2f7',
                        boxShadow: isDark ? '0 8px 30px rgba(0,0,0,0.45)' : '0 8px 24px rgba(97,37,123,0.06)', overflow: 'hidden',
                    }}>
                        {[
                            { v: '300+', l: 'Miembros', c: isDark ? '#FFD97C' : '#3564db' },
                            { v: '5+ Años', l: 'Trayectoria', c: '#3564db' },
                            { v: '98%', l: 'Satisfacción', c: '#3564db' },
                        ].map((s, i) => (
                            <div key={s.l} style={{
                                flex: 1, padding: '12px 8px', textAlign: 'center',
                                borderRight: i < 2 ? (isDark ? '1.5px solid rgba(193,52,139,0.2)' : '1.5px solid #f1f5f9') : 'none',
                            }}>
                                <div style={{ fontSize: '1.25rem', fontWeight: '900', color: s.c, lineHeight: 1, letterSpacing: '-0.02em' }}>{s.v}</div>
                                <div style={{ fontSize: '0.68rem', color: isDark ? '#cbd5e1' : '#94a3b8', fontWeight: '600', marginTop: '4px' }}>{s.l}</div>
                            </div>
                        ))}
                    </div>

                    {/* Cita testimonial sutil */}
                    <div style={{
                        marginTop: '16px', padding: '10px 16px', borderRadius: '12px',
                        background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(97,37,123,0.03)',
                        border: isDark ? '1px dashed rgba(193,52,139,0.25)' : '1px dashed rgba(97,37,123,0.15)',
                        fontSize: '0.76rem', color: isDark ? '#94a3b8' : '#64748b', fontStyle: 'italic',
                    }}>
                        "El espacio donde nacen y escalan las empresas más dinámicas de La Paz."
                    </div>

                    {/* BrandStripes decorativo */}
                    <div style={{ marginTop: '18px', display: 'flex', justifyContent: 'center' }}>
                        <BrandStripes size="sm" />
                    </div>
                </div>
            </div>

            {/* ── Panel derecho (formulario con BorderGlow) ── */}
            <div style={{
                flex: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 'clamp(20px, 4vw, 40px)',
                background: isDark ? '#0b0614' : '#fff',
                position: 'relative', overflow: 'hidden',
                transition: 'background 0.3s ease',
            }}>
                {/* Blob sutil de fondo */}
                <div style={{
                    position: 'absolute', bottom: '-120px', right: '-120px',
                    width: '400px', height: '400px', pointerEvents: 'none',
                    background: isDark
                        ? `radial-gradient(circle, rgba(193,52,139,0.12), transparent 70%)`
                        : `radial-gradient(circle, rgba(97,37,123,0.06), transparent 70%)`,
                    borderRadius: '50%',
                }} />

                <div style={{
                    width: '100%', maxWidth: '440px', position: 'relative',
                    animation: mounted ? 'none' : 'fadeUp 0.6s ease both',
                }}>
                    <BorderGlow
                        borderRadius={24}
                        backgroundColor={isDark ? '#150d24' : '#ffffff'}
                    >
                        <div style={{ padding: 'clamp(28px, 4vw, 40px)', width: '100%', position: 'relative' }}>
                            {/* Enlace para volver a la landing page */}
                            <div style={{ marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Link
                                    to="/"
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                        fontSize: '0.78rem', fontWeight: '700',
                                        color: isDark ? '#FFD97C' : '#3564db',
                                        textDecoration: 'none',
                                        transition: 'all 0.2s ease',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                                >
                                    <span>←</span> Volver al inicio
                                </Link>
                                <span style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#94a3b8', fontWeight: '600' }}>
                                    Co Marca Group
                                </span>
                            </div>
                            {/* Logo */}
                            <div style={{ textAlign: 'center', marginBottom: '28px', animation: 'fadeUp 0.5s ease both' }}>
                                <Link to="/" style={{ display: 'inline-block', textDecoration: 'none' }}>
                                    <img src={isDark ? logoDark : logo} alt="Co Marca" style={{
                                        height: isDark ? '29px' : '40px',
                                        margin: '0 auto',
                                        opacity: 1,
                                        transition: 'opacity 300ms ease',
                                    }}
                                        onError={e => {
                                            const el = e.currentTarget as HTMLImageElement;
                                            el.style.display = 'none';
                                            const next = el.nextElementSibling as HTMLElement;
                                            if (next) next.style.display = 'flex';
                                        }}
                                    />
                                    {/* Fallback logo */}
                                    <div style={{
                                        display: 'none', alignItems: 'center', gap: '10px',
                                        justifyContent: 'center',
                                    }}>
                                        <div style={{
                                            width: '40px', height: '40px', borderRadius: '12px',
                                            background: 'linear-gradient(135deg, #3564db, #3564db)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '0.85rem', fontWeight: '900', color: '#fff',
                                        }}>CM</div>
                                        <span style={{ fontSize: '1.2rem', fontWeight: '900', color: isDark ? '#ffffff' : '#0f172a', letterSpacing: '-0.02em' }}>
                                            Co<span style={{ color: '#3564db' }}>Marca</span>
                                        </span>
                                    </div>
                                </Link>
                            </div>

                            {/* Header */}
                            <div style={{ marginBottom: '24px', animation: 'fadeUp 0.55s ease 0.05s both' }}>
                                {/* Barra de colores */}
                                <div style={{ display: 'flex', gap: '5px', marginBottom: '18px' }}>
                                    {['#3564db', '#3564db', '#3564db', '#EA8406'].map((c, i) => (
                                        <div key={i} style={{ height: '3px', width: i === 0 ? '28px' : '10px', borderRadius: '3px', background: c }} />
                                    ))}
                                </div>
                                <h1 style={{
                                    fontSize: 'clamp(1.5rem, 3vw, 1.9rem)', fontWeight: '900',
                                    color: isDark ? '#ffffff' : '#0f172a', letterSpacing: '-0.025em', lineHeight: 1.15, marginBottom: '8px',
                                }}>
                                    Bienvenido de <span style={{ color: isDark ? '#FFD97C' : '#3564db' }}>vuelta</span>
                                </h1>
                                <p style={{ fontSize: '0.88rem', color: isDark ? '#cbd5e1' : '#64748b', fontWeight: '500', lineHeight: '1.6' }}>
                                    Ingresa tus credenciales para acceder a tu cuenta
                                </p>
                            </div>

                            {/* Form */}
                            <form onSubmit={handleSubmit} style={{ animation: 'fadeUp 0.6s ease 0.1s both' }}>

                                {/* Email */}
                                <div style={{ marginBottom: '16px' }}>
                                    <label htmlFor="login-email" style={{
                                        display: 'block', fontSize: '0.78rem', fontWeight: '700',
                                        color: emailFocused ? (isDark ? '#FFD97C' : '#3564db') : (isDark ? '#e2e8f0' : '#374151'),
                                        marginBottom: '8px', letterSpacing: '0.02em',
                                        transition: 'color 0.2s',
                                    }}>
                                        Correo electrónico
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <div style={{
                                            position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)',
                                            color: emailFocused ? (isDark ? '#FFD97C' : '#3564db') : (isDark ? '#cbd5e1' : '#94a3b8'),
                                            transition: 'color 0.2s', pointerEvents: 'none',
                                        }}>
                                            <Mail size={16} strokeWidth={2} />
                                        </div>
                                        <input
                                            id="login-email"
                                            name="email"
                                            type="email"
                                            autoComplete="username"
                                            required
                                            disabled={isLoading}
                                            className="auth-input"
                                            placeholder="tu@correo.com"
                                            onFocus={() => setEmailFocused(true)}
                                            onBlur={() => setEmailFocused(false)}
                                            style={{
                                                width: '100%',
                                                padding: '12px 16px 12px 42px',
                                                borderRadius: '14px',
                                                border: emailFocused
                                                    ? (isDark ? '1.5px solid #3564db' : '1.5px solid #3564db')
                                                    : (isDark ? '1.5px solid rgba(193,52,139,0.3)' : '1.5px solid #f1f5f9'),
                                                background: emailFocused
                                                    ? (isDark ? '#1f1333' : '#fff')
                                                    : (isDark ? '#120920' : '#f8fafc'),
                                                fontSize: '0.88rem',
                                                color: isDark ? '#ffffff' : '#0f172a',
                                                fontFamily: "'DM Sans', sans-serif",
                                                boxShadow: emailFocused ? '0 0 0 3px rgba(193,52,139,0.25)' : 'none',
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Password */}
                                <div style={{ marginBottom: '8px' }}>
                                    <label htmlFor="login-password" style={{
                                        display: 'block', fontSize: '0.78rem', fontWeight: '700',
                                        color: passFocused ? (isDark ? '#FFD97C' : '#3564db') : (isDark ? '#e2e8f0' : '#374151'),
                                        marginBottom: '8px', letterSpacing: '0.02em',
                                        transition: 'color 0.2s',
                                    }}>
                                        Contraseña
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            id="login-password"
                                            type={showPassword ? 'text' : 'password'}
                                            required
                                            autoComplete="current-password"
                                            name="password"
                                            disabled={isLoading}
                                            className="auth-input"
                                            placeholder="Tu contraseña"
                                            onFocus={() => setPassFocused(true)}
                                            onBlur={() => setPassFocused(false)}
                                            style={{
                                                width: '100%',
                                                padding: '12px 44px 12px 16px',
                                                borderRadius: '14px',
                                                border: passFocused
                                                    ? (isDark ? '1.5px solid #3564db' : '1.5px solid #3564db')
                                                    : (isDark ? '1.5px solid rgba(193,52,139,0.3)' : '1.5px solid #f1f5f9'),
                                                background: passFocused
                                                    ? (isDark ? '#1f1333' : '#fff')
                                                    : (isDark ? '#120920' : '#f8fafc'),
                                                fontSize: '0.88rem',
                                                color: isDark ? '#ffffff' : '#0f172a',
                                                fontFamily: "'DM Sans', sans-serif",
                                                boxShadow: passFocused ? '0 0 0 3px rgba(193,52,139,0.25)' : 'none',
                                            }}
                                        />
                                        <button
                                            type="button"
                                            aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                            aria-pressed={showPassword}
                                            aria-controls="login-password"
                                            onClick={() => setShowPassword(visible => !visible)}
                                            style={{
                                                position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)',
                                                background: 'none', border: 'none', cursor: 'pointer',
                                                color: passFocused ? (isDark ? '#FFD97C' : '#3564db') : (isDark ? '#cbd5e1' : '#94a3b8'),
                                                transition: 'color 0.2s', padding: '2px',
                                            }}
                                        >
                                            {showPassword ? <LockOpen size={16} strokeWidth={2} /> : <Lock size={16} strokeWidth={2} />}
                                        </button>
                                    </div>
                                </div>

                                {/* Error */}
                                {error && (
                                    <div style={{
                                        display: 'flex', alignItems: 'center', gap: '10px',
                                        background: isDark ? 'rgba(193, 52, 139,0.15)' : 'rgba(97,37,123,0.08)',
                                        border: isDark ? '1.5px solid rgba(193, 52, 139,0.4)' : '1.5px solid rgba(97,37,123,0.25)',
                                        borderRadius: '12px', padding: '12px 14px',
                                        marginBottom: '16px', marginTop: '8px',
                                        animation: 'fadeIn 0.3s ease',
                                    }}>
                                        <div style={{
                                            width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
                                            background: isDark ? 'rgba(193, 52, 139,0.25)' : 'rgba(97,37,123,0.14)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <svg width="14" height="14" viewBox="0 0 20 20" fill={isDark ? '#3564db' : '#3564db'}>
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <span style={{ fontSize: '0.82rem', color: isDark ? '#ff94aa' : '#420f54', fontWeight: '600' }}>{error}</span>
                                    </div>
                                )}

                                {/* Submit */}
                                <div style={{textAlign:'right',margin:'12px 0 20px'}}><Link to="/recuperar-contrasena" className="auth-link" style={{color:isDark ? '#FFD97C' : '#3564db',fontSize:'.83rem',fontWeight:700}}>¿Olvidaste tu contraseña?</Link></div>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="auth-btn-submit"
                                    style={{
                                        width: '100%', marginTop: '20px',
                                        padding: '14px 24px',
                                        borderRadius: '14px',
                                        background: isLoading
                                            ? (isDark ? '#231538' : '#f1f5f9')
                                            : 'linear-gradient(135deg, #3564db 0%, #3564db 100%)',
                                        border: 'none',
                                        color: isLoading ? '#94a3b8' : '#fff',
                                        fontSize: '0.92rem', fontWeight: '800',
                                        cursor: isLoading ? 'not-allowed' : 'pointer',
                                        fontFamily: "'DM Sans', sans-serif",
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                        boxShadow: isLoading ? 'none' : '0 8px 24px rgba(193,52,139,0.4)',
                                        transition: 'all 0.25s cubic-bezier(.16,1,.3,1)',
                                        letterSpacing: '-0.01em',
                                    }}
                                >
                                    {isLoading ? (
                                        <>
                                            <div style={{
                                                width: '16px', height: '16px', borderRadius: '50%',
                                                border: `2px solid #94a3b820`,
                                                borderTop: `2px solid #94a3b8`,
                                                animation: 'spin 0.7s linear infinite',
                                            }} />
                                            Iniciando sesión...
                                        </>
                                    ) : (
                                        <>
                                            Iniciar Sesión
                                            <ArrowRight size={16} strokeWidth={2.5} />
                                        </>
                                    )}
                                </button>

                                {/* Link a register */}
                                <p style={{
                                    textAlign: 'center', marginTop: '24px',
                                    fontSize: '0.85rem', color: isDark ? '#cbd5e1' : '#64748b', fontWeight: '500',
                                }}>
                                    ¿No tienes cuenta?{' '}
                                    <Link
                                        to="/register"
                                        className="auth-link"
                                        style={{
                                            color: isDark ? '#FFD97C' : '#3564db', fontWeight: '700',
                                            textDecoration: 'none', transition: 'color 0.2s',
                                        }}
                                    >
                                        Regístrate gratis →
                                    </Link>
                                </p>
                            </form>
                        </div>
                    </BorderGlow>
                </div>
            </div>
        </div>
    );
};

export default Login;

